import React from 'react';
import { Tooltip, Icon, Icons } from '@nextiva/next-ui-components';
import { WaffleMenuContainer } from '@nextiva/next-ui-containers';
import { useFeature } from '@nextiva/feature-toggle';
import PropTypes from 'prop-types';
import {
  StyledGlobalHeader,
  StyledContentContainer,
  StyledWaffleMenuContainer,
  StyledStack,
  StyledLogo,
} from './GlobalHeader.styled';
import { getInitials, isActiveFeature } from '../../utils';
import { AvatarMenu } from '../AvatarMenu';
import { useAxios } from '../../hooks/useAxios';
import { SupportMenu } from '../SupportMenu';
import { IconButton } from '../IconButton';
import { flags, svg } from '../../constants';

const id = 'global-header';
const blockName = 'NuiGlobalHeader';

export const GlobalHeader = ({
  firstName,
  lastName,
  globalStoreData,
  hasWaffleMenu,
  showLogo = true,
  hasCalendar,
  avatarMenuOptions,
  onSignOut,
  presenceStatus,
  children,
}) => {
  const { isEnabled } = useFeature({ name: flags.crmCalendar });
  const isReady = useAxios(globalStoreData.sso);

  if (!isReady) {
    return null;
  }

  const hasCalendarBaseFeature = isActiveFeature('CALENDAR_BASE_FEATURE', globalStoreData.licenses);

  let initials;

  if (firstName || lastName) {
    initials = getInitials(firstName, lastName);
  } else if (globalStoreData.user.firstName || globalStoreData.user.lastName) {
    initials = getInitials(globalStoreData.user.firstName, globalStoreData.user.lastName);
  } else {
    initials = getInitials(globalStoreData.user.profileDTO.displayName);
  }

  return (
    <StyledGlobalHeader id={id} className={`${blockName}-root`} hasLeftPadding={!hasWaffleMenu}>
      {hasWaffleMenu && (
        <StyledWaffleMenuContainer
          id={`${id}-waffle-menu-container`}
          className={`${blockName}-waffleMenuContainer`}
        >
          <WaffleMenuContainer
            id={`${id}-waffle-button`}
            globalStoreData={globalStoreData}
            data-testid="GlobalHeader-GlobalHeader-WaffleMenuButton"
          />
        </StyledWaffleMenuContainer>
      )}
      {showLogo && <StyledLogo src={svg.logo} alt="Nextiva" />}
      <StyledContentContainer id={`${id}-content-container`} className={`${blockName}-content`}>
        {children}
      </StyledContentContainer>
      <StyledStack
        id={`${id}-action-container`}
        direction="row"
        alignItems="center"
        className={`${blockName}-actionMenuContainer`}
      >
        {hasCalendar && isEnabled && hasCalendarBaseFeature && (
          <Tooltip placement="bottom" title="Calendar">
            <IconButton
              aria-label="open calendar"
              id={`${id}-calendar-button`}
              component="a"
              href="/apps/calendar"
            >
              <Icon icon={Icons.calendar} htmlColor="#758BA3" />
            </IconButton>
          </Tooltip>
        )}
        <SupportMenu />
        <AvatarMenu
          globalStoreData={globalStoreData}
          menuOptions={avatarMenuOptions}
          onSignOut={onSignOut}
          presenceStatus={presenceStatus}
        >
          {initials}
        </AvatarMenu>
      </StyledStack>
    </StyledGlobalHeader>
  );
};

GlobalHeader.propTypes = {
  globalStoreData: PropTypes.object,
  hasCalendar: PropTypes.bool,
  hasWaffleMenu: PropTypes.bool,
  showLogo: PropTypes.bool,
  avatarMenuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  onSignOut: PropTypes.func,
  presenceStatus: PropTypes.string,
};

GlobalHeader.displayName = 'GlobalHeader';
